import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Lightweight prompt modal for RichTextEditor toolbar actions.
 * Not a <form> — portals bubble submit into parent BlogForm otherwise.
 * fields: [{ name, label, type?, defaultValue?, placeholder?, min?, max?, required? }]
 */
export function EditorPromptModal({
  open,
  title,
  description,
  confirmLabel = "Apply",
  cancelLabel = "Cancel",
  fields = [],
  onConfirm,
  onCancel,
}) {
  const titleId = useId();
  const firstInputRef = useRef(null);
  const [values, setValues] = useState(() => {
    const next = {};
    fields.forEach((field) => {
      next[field.name] =
        field.defaultValue == null ? "" : String(field.defaultValue);
    });
    return next;
  });

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => firstInputRef.current?.focus(), 30);
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onCancel]);

  if (!open || typeof document === "undefined") return null;

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const apply = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    for (const field of fields) {
      if (!field.required) continue;
      const value = String(values[field.name] ?? "").trim();
      if (!value) {
        firstInputRef.current?.focus();
        return;
      }
    }
    onConfirm?.(values);
  };

  return createPortal(
    <div
      className="gp-rte-prompt"
      role="presentation"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="gp-rte-prompt-backdrop"
        aria-label="Close dialog"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCancel?.();
        }}
      />
      <div
        className="gp-rte-prompt-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="gp-rte-prompt-head">
          <h3 id={titleId}>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>

        <div className="gp-rte-prompt-body">
          {fields.map((field, index) => (
            <label key={field.name} className="gp-rte-prompt-field">
              <span>{field.label}</span>
              <input
                ref={index === 0 ? firstInputRef : undefined}
                type={field.type || "text"}
                name={field.name}
                value={values[field.name] ?? ""}
                placeholder={field.placeholder || ""}
                min={field.min}
                max={field.max}
                required={Boolean(field.required)}
                onChange={(e) => setField(field.name, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    apply(e);
                  }
                }}
              />
            </label>
          ))}
        </div>

        <div className="gp-rte-prompt-actions">
          <button
            type="button"
            className="gp-rte-prompt-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCancel?.();
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="gp-rte-prompt-btn gp-rte-prompt-btn-primary"
            onClick={apply}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
